import { useEffect } from 'react';
import { useUser } from '%hooks/useUser';

import {
	GetUserConversationsDocument,
	GetUserConversationsQuery,
} from '%screens/chats/components/__generated__/conversations.generated';
import { useNewMessageSubscription } from './__generated__/newMessage.subscription.generated';
import useUnreadConversationsStore from './unreadConversations.store';
import { useGetUnreadConversationIdsQuery } from './__generated__/unreadConversationIds.generated';
export const useConversations = (_searchedText?: string) => {
	const user = useUser();
	const { data: unreadData } = useGetUnreadConversationIdsQuery();
	const { setUnreadConversationIds, addUnreadConversationId } = useUnreadConversationsStore();

	useEffect(() => {
		setUnreadConversationIds(unreadData?.currentUser.unreadConversationIds ?? new Set());
	}, [unreadData?.currentUser.unreadConversationIds, setUnreadConversationIds]);

	useNewMessageSubscription({
		// TODO need to extract this. Maybe another context with all live updated data from sockets?
		variables: { userId: user?.id.toString() },
		onData: ({ client, data: subscriptionData }) => {
			if (!subscriptionData.data?.newMessage) {
				return;
			}
			const {
				newMessage: { conversationId, ...incomingMessage },
			} = subscriptionData.data;

			addUnreadConversationId(Number(conversationId));

			const cachedQuery = client.cache.readQuery<GetUserConversationsQuery>({
				query: GetUserConversationsDocument,
			});

			if (!cachedQuery) {
				return;
			}

			const cachedConversations = cachedQuery.currentUser.conversations;
			const conversationToUpdate = cachedConversations.find(({ id }) => id === conversationId);
			if (!conversationToUpdate) {
				return client.refetchQueries({
					include: [GetUserConversationsDocument],
				});
			}

			const { lastMessage } = conversationToUpdate;
			if (lastMessage && lastMessage.createdAt > incomingMessage.createdAt) {
				return;
			}

			const newLastMessage = {
				...incomingMessage,
				__typename: 'ChatMessage',
			} as const;

			const updatedConversations = [
				{ ...conversationToUpdate, lastMessage: newLastMessage },
				...cachedConversations.filter(
					(conversation) => conversation.id !== conversationToUpdate.id,
				),
			];

			return client.writeQuery<GetUserConversationsQuery>({
				query: GetUserConversationsDocument,
				data: {
					currentUser: {
						...cachedQuery.currentUser,
						conversations: updatedConversations,
					},
				},
			});
		},
	});
};
